import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { loginUser, registerUser, getMe, verifyEmail as verifyEmailApi } from "@/lib/api";
import i18n from "@/lib/i18n";

const AuthContext = createContext(null);

// DEV: Auto-login credentials (disabled — use login page)
const AUTO_LOGIN = false;
const AUTO_LOGIN_EMAIL = "";
const AUTO_LOGIN_PASSWORD = "";

// Sync i18n language when user data changes
function syncLanguage(userData) {
  if (userData?.language) {
    const lang = userData.language;
    localStorage.setItem("language", lang);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("les_lex_user");
      const parsed = stored ? JSON.parse(stored) : null;
      // Sync language on initial load from localStorage
      if (parsed?.language) syncLanguage(parsed);
      return parsed;
    } catch {
      // Corrupted localStorage — clear and start fresh
      localStorage.removeItem("les_lex_user");
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("les_lex_token");
    if (token) {
      getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("les_lex_user", JSON.stringify(res.data));
          syncLanguage(res.data);
        })
        .catch(() => {
          localStorage.removeItem("les_lex_token");
          localStorage.removeItem("les_lex_user");
          // If auto-login enabled, try to re-authenticate
          if (AUTO_LOGIN) {
            doAutoLogin();
            return;
          }
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else if (AUTO_LOGIN) {
      doAutoLogin();
    } else {
      setLoading(false);
    }

    function doAutoLogin() {
      loginUser({ email: AUTO_LOGIN_EMAIL, password: AUTO_LOGIN_PASSWORD, totp_code: "" })
        .then((res) => {
          const { access_token, user: userData } = res.data;
          localStorage.setItem("les_lex_token", access_token);
          localStorage.setItem("les_lex_user", JSON.stringify(userData));
          setUser(userData);
        })
        .catch((err) => {
          // Auto-login failed — clear stale token
          setUser(null);
        })
        .finally(() => setLoading(false));
    }
  }, []);

  const login = useCallback(async (email, password, totpCode = "") => {
    const res = await loginUser({ email, password, totp_code: totpCode });
    const { access_token, refresh_token, user: userData, requires_2fa } = res.data;

    if (requires_2fa) {
      return { requires_2fa: true, user: userData };
    }

    localStorage.setItem("les_lex_token", access_token);
    if (refresh_token) localStorage.setItem("les_lex_refresh_token", refresh_token);
    localStorage.setItem("les_lex_user", JSON.stringify(userData));
    setUser(userData);
    syncLanguage(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name, captchaToken = "") => {
    const data = { email, password, name };
    if (captchaToken) data.captcha_token = captchaToken;
    const res = await registerUser(data);
    const { requires_verification, email: regEmail } = res.data;
    if (requires_verification) {
      // Don't save tokens — user must verify email first
      return { requiresVerification: true, email: regEmail || email };
    }
    // Fallback for backward compat (shouldn't happen with new backend)
    const { access_token, refresh_token, user: userData } = res.data;
    if (access_token) {
      localStorage.setItem("les_lex_token", access_token);
      if (refresh_token) localStorage.setItem("les_lex_refresh_token", refresh_token);
      localStorage.setItem("les_lex_user", JSON.stringify(userData));
      setUser(userData);
    }
    return res.data;
  }, []);

  const verifyEmailToken = useCallback(async (token) => {
    const res = await verifyEmailApi(token);
    const { access_token, refresh_token, user: userData } = res.data;
    localStorage.setItem("les_lex_token", access_token);
    if (refresh_token) localStorage.setItem("les_lex_refresh_token", refresh_token);
    localStorage.setItem("les_lex_user", JSON.stringify(userData));
    setUser(userData);
    syncLanguage(userData);
    return userData;
  }, []);

  const loginWithToken = useCallback((token, userData, refreshToken) => {
    localStorage.setItem("les_lex_token", token);
    if (refreshToken) localStorage.setItem("les_lex_refresh_token", refreshToken);
    localStorage.setItem("les_lex_user", JSON.stringify(userData));
    setUser(userData);
    syncLanguage(userData);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem("les_lex_refresh_token");
    // Try to invalidate refresh token on server
    if (refreshToken) {
      try {
        const { default: axios } = await import("axios");
        const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8001"}/api`;
        const token = localStorage.getItem("les_lex_token");
        await axios.post(`${API}/auth/logout`, { refresh_token: refreshToken }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {}
    }
    localStorage.removeItem("les_lex_token");
    localStorage.removeItem("les_lex_refresh_token");
    localStorage.removeItem("les_lex_user");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      localStorage.setItem("les_lex_user", JSON.stringify(res.data));
      syncLanguage(res.data);
    } catch {}
  }, []);

  const isSuspended = user?.account_status === "suspended";

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, register, verifyEmailToken, logout, refreshUser, isSuspended }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
